import {
    AccountInvitation,
    Team,
    TeamMember
} from "../../API";
import {
    AccountInvitationStatus,
    Organization,
    TeamType,
    User
} from "../../models";
import {
    useMutation,
    useQuery,
    useQueryClient
} from "react-query";

import GraphQLOrganizationDAOFactory from "../../lib/organization/graphQL/GraphQLOrganizationDAOFactory";
import { GraphQLUserDAOFactory } from "../../lib/user/GraphQLUserDAOFactory";
import { graphQLAccountInvitationDAO } from "../../lib/accountInvitation/dao/GraphQLAccountInvitationDAO";
import { graphQLTeamDAO } from "../../lib/team/dao/GraphQLTeamDAO";
import { graphQLTeamManagerClient } from "../../lib/team/client/GraphQLTeamManagerClient";
import { graphQLTeamMemberDAO } from "../../lib/teamMember/dao/GraphQLTeamMemberDAO";
import { useSnackbar } from "notistack";
import { useUser } from "../../components/user/UserContext";

// TODO inject services
export const useTeamManagement = () => {
    const { organizationId, user } = useUser();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();

    const { data: organization } = useQuery<Organization>({
        queryKey: ['organization', organizationId],
        enabled: !!organizationId,
        queryFn: async () => {
            return await GraphQLOrganizationDAOFactory.getInstance().getById(organizationId ?? "");
        }
    });

    const { data: teams } = useQuery<Team[]>({
        queryKey: ['teams', organizationId],
        enabled: !!organizationId,
        queryFn: async () => {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            return await graphQLTeamDAO.listByOrganizationId(organizationId);
        },
        keepPreviousData: true
    });

    const teamIds = teams?.map(team => team.id);

    const { data: teamMembers } = useQuery<{ teamId: string, members: TeamMember[] }[]>({
        queryKey: ['teamMembers', teamIds],
        enabled: !!teamIds,
        queryFn: async () => {
            const teamMembers = await Promise.all((teamIds ?? []).map(async teamId => {
                return { teamId: teamId, members: await graphQLTeamMemberDAO.listByTeamId(teamId) };
            }));
            return teamMembers;
        },
        keepPreviousData: true
    });

    const { data: users } = useQuery<User[]>({
        queryKey: ['users', teamMembers?.map(tm => tm.members.map(m => m.userId)).flat() ?? []],
        enabled: !!teamMembers,
        queryFn: async () => {
            const userIds = teamMembers?.map(tm => tm.members.map(m => m.userId)).flat() ?? [];
            const users = await Promise.all(userIds.map(async userId => {
                return await GraphQLUserDAOFactory.getInstance().getUserById(userId);
            }));
            return users;
        },
        keepPreviousData: true
    });

    const { data: invitations } = useQuery<AccountInvitation[]>({
        queryKey: ['accountInvitations', organizationId],
        enabled: !!organizationId,
        queryFn: async () => {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            return await graphQLAccountInvitationDAO.getAccountInvitationsByOrganizationId(organizationId, AccountInvitationStatus.PENDING);
        },
        keepPreviousData: true
    });

    const inviteUserToOrganization = useMutation({
        mutationFn: async ({ emails, targetRole }: { emails: string[]; targetRole: TeamType }) => {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            return await graphQLAccountInvitationDAO.inviteUserToOrganization(organizationId, emails, targetRole);
        },
        onSuccess: async () => {
            await Promise.all([
                queryClient.invalidateQueries(['teamMembers']),
                queryClient.invalidateQueries(['users']),
                queryClient.invalidateQueries(['accountInvitations', organizationId])
            ]);
        },
        onError: () => {
            snackbar.enqueueSnackbar('Failed to invite user', { variant: 'error' });
        }
    });

    const getUserById = (userId: string) => {
        return users?.find(user => user.id === userId);
    };

    const ownerTeamMembers = teams?.filter(team => team.type === TeamType.OWNER)
        .map(team => {
            const teamMembersList = teamMembers?.find(tm => tm.teamId === team.id)?.members ?? [];
            return teamMembersList;
        })
        .flat();

    const ownerTeamMemberIds = ownerTeamMembers?.map(member => member.userId);

    const memberTeamMembers = teams?.filter(team => team.type === TeamType.MEMBER)
        .map(team => {
            const teamMembersList = teamMembers?.find(tm => tm.teamId === team.id)?.members ?? [];
            return teamMembersList;
        })
        .flat().filter(member => !ownerTeamMemberIds?.includes(member.userId));

    const ownerTeamId = teams?.find(team => team.type === TeamType.OWNER)?.id;
    const memberTeamId = teams?.find(team => team.type === TeamType.MEMBER)?.id;

    const isAccountOwner = ownerTeamMembers?.some(member => member.userId === user.getUsername());

    const handleRoleChange = async (member: TeamMember, newRole: TeamType) => {
        try {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            await graphQLTeamManagerClient.changeRole(organizationId, member.userId, newRole);
            // Invalidate relevant queries to trigger a refetch
            await queryClient.invalidateQueries(['teams', organizationId]);
            await queryClient.invalidateQueries(['teamMembers']);
            snackbar.enqueueSnackbar('Role changed successfully', { variant: 'success' });
            return true;
        } catch (error) {
            snackbar.enqueueSnackbar(`Failed to change role for user ${member.userId} to ${newRole}`, { variant: 'error' });
            return false;
        }
    };

    const handleRemoveMember = async (member: TeamMember) => {
        try {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            await graphQLTeamManagerClient.removeMember(organizationId, member.userId);
            // Invalidate relevant queries to trigger a refetch
            await queryClient.invalidateQueries(['teams', organizationId]);
            await queryClient.invalidateQueries(['teamMembers']);
            snackbar.enqueueSnackbar('Member removed successfully', { variant: 'success' });
        } catch (error) {
            snackbar.enqueueSnackbar(`Failed to remove member ${member.userId} from team`, { variant: 'error' });
        }
    };
    const removeInvitation = useMutation({
        mutationFn: async (invitation: AccountInvitation) => {
            return await graphQLAccountInvitationDAO.removeInvitation(invitation.id, invitation._version);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['accountInvitations', organizationId]);
            snackbar.enqueueSnackbar('Invitation removed successfully', { variant: 'success' });
        },
        onError: () => {
            snackbar.enqueueSnackbar('Failed to remove invitation', { variant: 'error' });
        }
    });

    const isOwner = ownerTeamMembers?.some(member => member.userId === user.getUsername());
    const isMember = memberTeamMembers?.some(member => member.userId === user.getUsername());
    const userRole = isOwner ? TeamType.OWNER : isMember ? TeamType.MEMBER : null;

    return {
        ownerTeamMembers,
        memberTeamMembers,
        getUserById,
        invitations,
        ownerTeamId,
        memberTeamId,
        isAccountOwner,
        handleRoleChange,
        handleRemoveMember,
        inviteUserToOrganization,
        organization,
        userRole,
        removeInvitation
    };
};